import React, { useState, useEffect } from "react";
import {
  analyzePlaces,
  getPlacesByFlags as getPlaces,
  getSummary,
} from "../../../api/googleBusinessProfileApi";
import translate from "../../../config/translations/translate";
import "./styles.css";
import { useSelector } from "react-redux";
import { FirstConfigPageContainer } from "./FirstConfigPage";
import { LoadingPageContainer } from "./LoadingPage";
import { ErrorPageContainer } from "./ErrorPage";
import {
  Table,
  UxMetricDisplay,
  UxScoreBar,
  UxCallToActionImproveImpactList,
  Filters,
} from "botbit-ui-components";
import "bootstrap/dist/js/bootstrap.bundle";
import withEventTracking, { trackers } from "../../../config/withEventTracking";
import TimeAgo from "../../../commons/components/utils/TimeAgo";
import { getProcessStatus } from "../../../api/processApi";
import ScoreScreen from "./ScoreDescription/ScoreScreen";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import PostModalContainer from "./details/PostModal/PostModalContainer";
import MobileScreenUnavailableMessage from '../../../commons/components/application/MobileScreenUnavailableMessage';
import Swal from 'sweetalert2';
import { useNotificationTracking } from "../../../commons/utils/useNotificationTracking"; 

const buildCaption = (summary, t) => ({
  caption: {
    key: "caption-main",
    title: t("summary.captionTitle"),
    elements: [
      {
        key: "keywords-element",
        icon: "tag",
        value: summary.numberOfPlaces - summary.placesWithoutKeywords,
        description: t("summary.categoriesCorrect"),
        highlighted: {
          text: "string",
          color: "danger",
        },
        totalCount: `/${summary.numberOfPlaces}`,
      },
      {
        key: "posts-element",
        icon: "sticky-note",
        value: summary.numberOfPlaces - summary.placesWithoutPosts,
        description: (
          <div key="posts-description">
            {t("summary.categoriesCorrectWithGooglePosts")}
            <br />
            {t("summary.last15")}
          </div>
        ),
        highlighted: {
          text: "string",
          color: "danger",
        },
        totalCount: `/${summary.numberOfPlaces}`,
      },
      {
        key: "photos-element",
        icon: "camera",
        value: summary.numberOfPlaces - summary.placesWithoutPhotos,
        description: t("summary.categoriesCorrectWithFewPhotos"),
        highlighted: {
          text: "string",
          color: "danger",
        },
        totalCount: `/${summary.numberOfPlaces}`,
      },
      {
        key: "website-element",
        icon: "globe",
        value: summary.numberOfPlaces - summary.placesWithoutWebsite,
        description: t("summary.categoriesCorrectWithWebsite"),
        highlighted: {
          text: "string",
          color: "danger",
        },
        totalCount: `/${summary.numberOfPlaces}`,
      },
    ],
  },
});

const metricsConfiguration = (summary, t, openModal, openPostModal) => ({
  key: "metrics-config",
  outstanding: {
    key: "outstanding-section",
    icon: "",
    content: "React.Component",
    value: summary.score || 0,
    title: t("summary.currentScore"),
    showBar: true,
  },
  caption: {
    key: "caption-section",
    ...buildCaption(summary, t, openModal, openPostModal).caption,
  },
  scoreInfoLink: {
    key: "score-info-section",
    text: t("score.howItWorks"),
    onclickHandler: openModal,
  },
});

const BusinessProfilesTable = ({ t, trackEvent }) => {
  const history = useHistory();
  const selectedCompany = useSelector(
    (state) => state.application.selectedCompany
    
  );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);  
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(window.innerWidth < 1000);  
  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);
  const [firstTime, setFirstTime] = useState(false);
  const [summary, setSummary] = useState();
  const [isOpenn, setIsOpenn] = useState(false);
  const [progress, setProgress] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [lastAnalysisDate, setLastAnalysisDate] = useState();

  const [processInfo, setProcessInfo] = useState({ running: false, processId: null });

  const [filterValues, setFilterValues] = useState({
    combinedFilter: [],
  });

  useNotificationTracking(trackEvent, 'local_seo_gbp');

  useEffect(() => {
    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
        window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (windowWidth < 1000) {
      setIsMobileModalOpen(true);
    } else {
      setIsMobileModalOpen(false);
    }
  }, [windowWidth]);

  // Si hay un proceso corriendo detectado desde el backend, iniciar polling con ese processId
  useEffect(() => {
    if (processInfo.running && processInfo.processId) {
      checkAnalyzeStatus(processInfo.processId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processInfo.running, processInfo.processId]);

  

  const filtersConfig = [
    { id: "withoutKeywords", label: t("withoutKeywords") },
    { id: "withoutPosts", label: t("withoutPosts") },
    { id: "withoutPhotos", label: t("withoutPhotos") },
    { id: "withoutPhone", label: t("withoutPhone") },
    { id: "withoutWebsite", label: t("withoutWebsite") },
  ];

  const schemas = {
    combinedFilter: {
      id: "combinedFilter",
      label: t("filterBy"),
      type: "multiselect",
      options: filtersConfig.map((filter) => ({
        value: filter.id,
        text: filter.label,
      })),
      defaultValues: [],
      visible: true,
      priority: true,
    },
  };

  const handleFilterChange = (newValues) => {
    setFilterValues((prev) => {
      return {
        ...prev,
        ...newValues,
      };
    });
  };

  const columns = [
    {
      dataField: "score",
      title: t("score"),
      headerClassName: "text-uppercase",
      formatter: (cellContent, source) => (
        <div className={`score-table ${source.insignia ? 'local-insignia' : ''}`}> 
          <UxScoreBar value={cellContent} showIcon={true} highlighted={source.insignia} />
        </div>
      ),
      size: 15,
    },
    {
      dataField: "name",
      title: t("titleAndVerification"),
      headerClassName: "text-uppercase",
      formatter: (cellContent, source) => (
        <div className="mt-0">
          <span className="font-bold text-black fs-14">{cellContent}</span>{" "}
          <br/>
          <span className="fs-14">{source.address}</span>
          <div className="mb-2"></div>
          <svg
            width="14"
            height="15"
            viewBox="0 0 14 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.65619 0.945559C6.84902 0.762939 7.15098 0.762939 7.34381 0.945559C8.68832 2.21887 10.5025 2.99909 12.5 2.99909C12.5318 2.99909 12.5635 2.9989 12.5952 2.9985C12.8134 2.99579 13.0082 3.13496 13.0764 3.3423C13.3514 4.17881 13.5 5.07211 13.5 5.99912C13.5 9.96047 10.7906 13.2877 7.12463 14.2313C7.04288 14.2523 6.95713 14.2523 6.87537 14.2313C3.20937 13.2877 0.5 9.96047 0.5 5.99912C0.5 5.07211 0.648582 4.17881 0.92361 3.3423C0.99178 3.13496 1.18656 2.99579 1.40481 2.9985C1.43649 2.9989 1.46822 2.99909 1.5 2.99909C3.4975 2.99909 5.31168 2.21887 6.65619 0.945559ZM7 4.99908C7.27614 4.99908 7.5 5.22294 7.5 5.49908V7.99908C7.5 8.27523 7.27614 8.49908 7 8.49908C6.72386 8.49908 6.5 8.27523 6.5 7.99908V5.49908C6.5 5.22294 6.72386 4.99908 7 4.99908ZM7 9.49908C6.72386 9.49908 6.5 9.72294 6.5 9.99908V10.0041C6.5 10.2802 6.72386 10.5041 7 10.5041H7.005C7.28114 10.5041 7.505 10.2802 7.505 10.0041V9.99908C7.505 9.72294 7.28114 9.49908 7.005 9.49908H7Z"
              fill={source.verified ? "#1EB8FF" : "gray"}
            />
          </svg>
          <span
            className={"ml-1 " + (source.verified ? "text-blue" : "text-gray")}
            style={{ fontWeight: 700, fontSize: 11}}
          >
            {source.verified ? t("verified") : t("notVerified")}
          </span>
        </div>
      ),
      size: 24,
    },
    {
      title: t("informationToUpdate"),
      dataField: "Informacion para ser actualizada",
      headerClassName: "text-uppercase",
      formatter: (cellContent, source) => {
        const items = [
          source.phoneValueIsOk ? null : (
            <span key="phoneValueIsOk" title={t("noPhone")}>
              {t("phone")}
            </span>
          ),
          source.websiteValueIsOk ? null : (
            <span key="website" title={t("noWebsite")}>
              {t("website")}
            </span>
          ),
          source.address ? null : (
            <span key="address" title={t("noAddress")}>
              {t("address")}
            </span>
          ),
          (!source.description || source.description.length < 150) ? (
            <span key="description" title={t("noDescription")}>
              {t("description")}
            </span>
          ) : null,
          source.primaryCategoryMatched && source.secondaryCategoryMatched ? null : (
            <span key="categories" title={t("noCategories")}>
              {t("categories")}
            </span>
          ),
        ].filter(Boolean);

        if (items.length === 0) {
          return (
            <div className="d-flex-column">
              <h6>{t("improvementOpportunities2")}</h6>
              <p className="all-updated-text mb-0">{t("allUpdated2")}</p>
            </div>
          );
        }

        return (
          <UxCallToActionImproveImpactList
            title={t("improvementOpportunities2")}
            items={items}
          />
        );
      },
      size: 22,
    },
    {
      title: t("improveContent"),
      dataField: "Contenido a mejorar",
      headerClassName: "text-uppercase",
      formatter: (cellContent, source) => {
        const items = [
          source.postHistoryCountValueIsOk ? null : (
            <span key="postHistoryCount" title={t("noPosts")}>
              {t("postHistoryCount")}
            </span>
          ),
          source.photosValueIsOk ? null : (
            <span key="photos" title={t("noPhoto")}>
              {t("photos")}
            </span>
          ),
          source.profilePhotoValueIsOk ? null : (
            <span key="logo" title={t("noLogo")}>
              {t("logo")}
            </span>
          ),
         
          source.coverPhotoValueIsOk ? null : (
            <span key="cover" title={t("noCover")}>
              {t("cover")}
            </span>
          ),
        ].filter(Boolean);

        if (items.length === 0) {
          return <h6>{t("allUpdated")}</h6>;
        }

        return (
          <UxCallToActionImproveImpactList
            title={t("improvementOpportunities")}
            items={items}
          />
        );
      },
      size: 22,
    },
    {
      title: t(""),
      dataField: "Accionables",
      headerClassName: "text-uppercase",

      formatter: (cellContent, source) => {
        const handleRedirect = () => {
          history.push(`/localseo/gbp/${source.id}/${source.socialLocationId}`);
        };

        return (
          <div className="button-container">
            <button className={source.insignia ? "btn-actionable-insignia w-100" : "table-button"} onClick={handleRedirect}>
              {source.insignia ?
              null : 
              <svg
              width="8"
              height="12"
              viewBox="0 0 8 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.90424 0.269187C1.95153 0.109594 2.09908 0 2.26667 0H5.28889C5.41032 0 5.52435 0.0579453 5.59536 0.155733C5.66636 0.253521 5.68568 0.379229 5.64728 0.493584L4.30192 4.49999H7.17777C7.32206 4.49999 7.45375 4.58157 7.51714 4.71023C7.58053 4.83889 7.56456 4.99216 7.47597 5.10522L2.18709 11.8552C2.07512 11.9981 1.8763 12.0412 1.71452 11.9576C1.55273 11.8741 1.47419 11.6877 1.52782 11.5147L2.88851 7.12498H0.377781C0.258722 7.12498 0.14663 7.06927 0.075303 6.97465C0.00397562 6.88002 -0.0182402 6.75755 0.015354 6.64417L1.90424 0.269187Z"
                fill="white"
              />
            </svg>}
              {source.insignia ? t("btnActionablesInsignia") : t("btnActionables")}
            </button>
          </div>
        );
      },
      size: 18,
    },
  ];

  const emmit = (action) =>
    trackEvent(action.toLowerCase(), {}, false, [
      trackers.BACKEND,
      trackers.MIXPANEL,
    ]);

  const showOptInSwal = (count) => {
    Swal.fire({
      title: t('optInTitle'),
      html: `<div style="text-align:left"><p><strong>${t('optInText1')} ${count} ${t('optInText2')}</strong></p></div>`,
      icon: 'warning',
      confirmButtonText: t('ok'),
      confirmButtonColor: '#007bff',
      allowOutsideClick: true,
      showCloseButton: true
    });
  };

  const showGoogleErrorSwal = () => {
    Swal.fire({
      title: t('googleErrorTitle'),
      text: t('googleErrorText'),
      icon: 'error',
      confirmButtonText: t('ok'),
      confirmButtonColor: '#007bff',
      allowOutsideClick: true,
      showCloseButton: true
    });
  };

  React.useEffect(() => {
    if (loading || firstTime) return;
    emmit("GBP_audit_viewed");
    getSummary(selectedCompany.id).then((res) => {
      if (res.data.data) {
        setSummary(res.data.data);
          const needsOptIn = (res.data.data.locationsNeedingOptIn || 0) > 0;
          if (needsOptIn) {
            showOptInSwal(res.data.data.locationsNeedingOptIn);
          }
          if (res.data.data.googleErrorsOccurred) {
            showGoogleErrorSwal();
          }
      }
    });
  }, [loading, firstTime]);

  const analyze = async () => {
    setFirstTime(false);
    setLoading(true);
    setSummary(undefined);
    //Evento de Mixpanel GBP_audit_scan
    trackEvent("GBP_audit_scan", {}, false, [trackers.MIXPANEL, trackers.INTERCOM]);

    try {
      const response = await analyzePlaces(selectedCompany.id);
      const processId = response.data.data.id;
      checkAnalyzeStatus(processId);
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  };
  

  const checkAnalyzeStatus = async (processId) => {
    try {
      const response = await getProcessStatus(processId);
      if (response.data.data) {
        const status = response.data.data.state;
        const currentProgress = response.data.data.progress || 0;

        setProgress((prev) => {
          return Math.max(prev, currentProgress);
        });
        if (status === "FINISHED_OK" || status === "FINISHED_ERROR") {
          setProgress(100);
          setTimeout(() => {
            setLoading(false);
            setProgress(0);
            setProcessInfo({ running: false, processId: null });
            getSummary(selectedCompany.id).then((res) => {
              if (res.data.data) {
                setSummary(res.data.data);
                  const needsOptIn = (res.data.data.locationsNeedingOptIn || 0) > 0;
                  if (needsOptIn) {
                    showOptInSwal(res.data.data.locationsNeedingOptIn);
                  }
                  if (res.data.data.googleErrorsOccurred) {
                    showGoogleErrorSwal();
                  }
              }
            });
            // Forzar refresco (re-ejecuta GET /gbp?companyId=...)
            setRefreshKey((prev) => prev + 1);
          }, 1000);
          if (status === "FINISHED_ERROR") {
            // Si el proceso terminó con error, informamos al usuario
            showGoogleErrorSwal();
          }
        } else if (status === "RUNNING") {
          setTimeout(() => checkAnalyzeStatus(processId), 5000);
        } else {
          setError("Unexpected error: " + status);
        }
      } else {
        setError("Invalid response");
      }
    } catch (e) {
      setError(e);
      setLoading(false);
    }
  };

  if (error) {
    return (
      <>
        <MobileScreenUnavailableMessage isOpen={isMobileModalOpen} />
        <ErrorPageContainer error={error} />
      </>
    );
  } else if (firstTime) {
    return (
      <>
        <MobileScreenUnavailableMessage isOpen={isMobileModalOpen} />
        <FirstConfigPageContainer analyze={analyze} />
      </>
    );
  } else if (loading) {
    return (
      <>
        <MobileScreenUnavailableMessage isOpen={isMobileModalOpen} />
        <LoadingPageContainer progress={progress} />
      </>
    );
  } else {
    const getTableData = (page, order, filters) =>
      getPlaces(page, order, {
        companyId: selectedCompany.id,
        ...filters,
      }).then((res) => {
        if (res.data.data) {
          if (res.data.data) {
            if (!res.data.data.lastAnalysisDate) setFirstTime(true);
            else setLastAnalysisDate(res.data.data.lastAnalysisDate);
            const running = !!res.data.data.analysisInProgress;
            const pid = res.data.data.runningProcessId || null;
            setProcessInfo({ running, processId: pid });
          }
          return res;
        }
      });

      const iconLocalInsignia = <svg width="17" height="17" viewBox="0 0 17 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8.33866 0L10.171 5.57393H16.1005L11.3034 9.01881L13.1357 14.5927L8.33866 11.1479L3.54157 14.5927L5.3739 9.01881L0.57681 5.57393H6.50633L8.33866 0Z" fill="#FABC42"/>
        </svg>

    const content = 
        <div className="botbit-table__header-insignia">
            <div className="botbit-table__container-icon-insignia">
                {iconLocalInsignia}
            </div>
            <span className="botbit-table__message-insignia">
                <span className='font-bold'>{t("contentLocalInsignia.textBold")}</span> {t("contentLocalInsignia.text")} <span className='font-bold'>{t("contentLocalInsignia.textBold2")}</span> {t("contentLocalInsignia.text2")}
            </span>
        </div>

    return (
      <div className="gbp-container">
        <MobileScreenUnavailableMessage isOpen={isMobileModalOpen} />
        <div className="title" style={{ position: "relative" }}>
          <div className="title-text">
            <img
              src="/static/img/gbp-icon.svg"
              alt="Icon"
              width="44"
              height="44"
            />
            &nbsp;&nbsp;
            <span
              style={{
                fontFamily: "Open Sans",
                fontSize: "28px",
                color: "1A1A1A",
              }}
            >
              {t("welcome")}
            </span>
            &nbsp;&nbsp;
            <span
              style={{
                fontFamily: "Open Sans",
                fontSize: "28px",
                color: "1A1A1A",
                fontWeight: "bold",
              }}
            >
              {t("title")}
            </span>
          </div>
          <div className="last-update">
            {processInfo.running ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#111827" }}>
                  <svg width="16" height="16" viewBox="0 0 50 50" aria-hidden="true">
                    <circle cx="25" cy="25" r="20" fill="none" stroke="#4F46E5" strokeWidth="5" strokeLinecap="round" strokeDasharray="31.415, 31.415">
                      <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="1s" repeatCount="indefinite" />
                    </circle>
                  </svg>
                  <strong>{t("rescanInBackground")}</strong>
                </span>
              </div>
            ) : (
              <>
                <span>
                  {t("analyze")}<TimeAgo date={lastAnalysisDate}></TimeAgo>
                </span>
                <button onClick={() => analyze()} className="refresh-button" disabled={processInfo.running || loading}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 5.84873C1.92525 4.24585 4.45215 2.16471 6.18404 1.51275C7.91592 0.860793 9.82024 0.829626 11.5725 1.42456C13.3248 2.01949 14.8166 3.20372 15.7938 4.77546C16.771 6.3472 17.1731 8.2092 16.9316 10.0442C16.69 11.8792 15.8198 13.5737 14.4692 14.8389C13.1186 16.1041 11.3712 16.8618 9.52463 16.9829C7.6781 17.1039 5.84673 16.5808 4.34256 15.5028C2.8384 14.4247 1.7545 12.8583 1.27556 11.0705M1 5.84873H5.84781M1 5.84873V1"
                      stroke="white"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
        {summary && (
          <>
            <div className="">
              <UxMetricDisplay
                data={metricsConfiguration(
                  summary,
                  t,
                  () => setIsOpenn(!isOpenn)
                  // openPostModal
                )}
              />
              <ScoreScreen
                isOpen={isOpenn}
                toggle={() => setIsOpenn(!isOpenn)}
              />
            </div>

            {/* <VideoModal
              description={t("summary.videoDescription")}
              title={t("summary.videoTitle")}
              subtitle={t("summary.videoSubtitle")}
              isOpen={isOpen}
              toggle={() => setIsOpen(!isOpen)}
              url={"https://www.youtube.com/watch?v=Qx8ElTKfE-g"}
            />
            <PostModal
              isOpen={isPostModalOpen !== undefined}
              toggle={() => setIsPostModalOpen(undefined)}
              title={t("createPost")}
              socialLocationId={isPostModalOpen}
            /> */}
          </>
        )}
        <div className="filterContainer">
          <Filters
            schemas={schemas}
            values={filterValues}
            onChange={handleFilterChange}
          />
        </div>
        
        <Table
          key={refreshKey}
          rowHighlighted={(element) => element.insignia === true}
          rowHighlightedContent={content}
          pagination={false}
          getData={getTableData}
          filterValues={filterValues}
          title={t("title")}
          subTitle={<p>{t("subtitle")}</p>}
          columns={columns}
          showMobileColumnTitles={true}
        />

        <PostModalContainer />
      </div>
    );
  }
};

BusinessProfilesTable.translations = {
  welcome: {
    en: "Optimize your ",
    es: "Optimiza tus ",
  },
  title: {
    en: "Google Business Profiles",
    es: "Google Business Profiles",
  },
  rescanInBackground: {
    en: "Rescan running in background",
    es: "Re‑scan ejecutándose en segundo plano",
  },
  optInTitle: {
    en: "Action required: Connect Google Business Profile",
    es: "Acción requerida: Conectar Google Business Profile",
  },
  optInText1: {
    en: "We detected ",
    es: "Detectamos ",
  },
  optInText2: {
    en: " location(s) that need Google opt‑in to continue.",
    es: " local(es) que necesitan hacer opt‑in de Google para continuar.",
  },
  googleErrorTitle: {
    en: "Google error",
    es: "Error de Google",
  },
  googleErrorText: {
    en: "There was a temporary error contacting Google. Please try again later.",
    es: "Ocurrió un error temporal con Google. Intentalo nuevamente más tarde.",
  },
  ok: {
    en: "OK",
    es: "Aceptar",
  },
  subtitle: {
    en: "Welcome to our new Google Business Profiles report!",
    es: "¡Bienvenido a nuestro nuevo reporte de Perfiles de Google My Business!",
  },
  searchLabel: {
    en: "Search",
    es: "Buscar por nombre, provincia, país",
  },
  searchPlaceholder: {
    en: "Search location",
    es: "Buscar ubicación",
  },
  verifiedInfo: {
    en: "This profile is verified by Google",
    es: "Este perfil está verificado por Google",
  },
  website: {
    en: "Website",
    es: "Sitio Web",
  },
  noWebSite: {
    en: "No website",
    es: "Sin sitio web",
  },
  reviews: {
    en: "Rate",
    es: "Rate",
  },
  photos: {
    en: "Photos",
    es: "Fotos",
  },
  logo: {
    en: "Logo",
    es: "Logo",
  },
  cover: {
    en: "Cover",
    es: "Portada",
  },
  categories: {
    en: "Categories",
    es: "Categorías",
  },
  howToEliminateProfile: {
    en: "How to eliminate this profile",
    es: "Cómo eliminar este perfil",
  },
  noCategories: {
    en: "No categories",
    es: "Sin categorías",
  },
  noPhone: {
    en: "No phone",
    es: "Sin teléfono",
  },
  name: {
    en: "Title",
    es: "Título",
  },
  titleAndVerification: {
    en: "Title and verification",
    es: "Título y verificacion",
  },
  informationToUpdate: {
    es: "Información para ser actualizada",
    en: "Information to be updated",
  },
  improveContent: {
    es: "Contenido a mejorar",
    en: "Content to improve",
  },
  improvementOpportunities: {
    es: "Oportunidades de mejora: ",
    en: "Improvement opportunities: ",
  },
  improvementOpportunities2: {
    es: "Atributos a mejorar: ",
    en: "Attributes to improve: ",
  },
  actionables: {
    es: "Accionables",
    en: "Actionables",
  },
  filterBy: {
    en: "Filter by: ",
    es: "Filtrar por: ",
  },
  allUpdated: {
    en: "Your content is up to date!",
    es: "¡Estás al día con contenido nuevo!",
  },
  allUpdated2: {
    en: "All attributes are correct!",
    es: "¡Todos los atributos son correctos!",
  },
  address: {
    en: "Address",
    es: "Dirección",
  },
  description: {
    en: "Description",
    es: "Descripción",
  },
  complete: {
    en: "Complete",
    es: "Completa",
  },
  noDescription: {
    en: "No description!",
    es: "¡Sin descripción!",
  },
  noPhoto: {
    en: "No photo!",
    es: "¡No tienes fotos!",
  },
  noLogo: {
    en: "No logo!",
    es: "¡No tienes logo!",
  },
  noCover: {
    en: "No cover!",
    es: "¡No tienes portada!",
  },
  
  duplicated: {
    en: "Duplicated",
    es: "Duplicado",
  },
  profileSelectLabel: {
    en: "Profiles",
    es: "Perfiles",
  },
  descriptionStatusLabel: {
    en: "Description",
    es: "Descripción",
  },
  incomplete: {
    en: "Incomplete",
    es: "Incompleta",
  },
  toExtensive: {
    en: "To extensive",
    es: "Muy larga",
  },
  none: {
    en: "None",
    es: "No tiene",
  },
  categoriesStatusLabel: {
    en: "Categories",
    es: "Categorías",
  },
  improvements: {
    en: "Category improvements",
    es: "Análisis de categorías",
  },
  improvementsSubtitle: {
    en: "Based on the queries your customers perform to find your location, we recommend changing the categories of your Google Business Profile as follows:",
    es: "Según las consultas que realizan sus clientes para encontrar su ubicación, le recomendamos cambiar las categorías de su perfil comercial de Google de la siguiente manera",
  },
  currentCategories: {
    en: "Current",
    es: "Actuales",
  },
  recomendation: {
    en: "Recommendation",
    es: "Recomendadas",
  },
  seeTutorial: {
    en: "See tutorial",
    es: "Ver tutorial",
  },
  categoriesMessage: {
    "text-danger": {
      es: "Necesitas agregar palabras clave!",
      en: "You need to add categories!",
    },
    "text-warning": {
      es: "Tus categorias pueden ser optimizadas",
      en: "Categories can be optimized",
    },
    "text-success": {
      es: "Tus palabras clave son correctas",
      en: "Your Categories are correct",
    },
  },
  seeImprovements: {
    es: "Sugeridas",
    en: "Suggested",
  },
  assignedKeywords: {
    en: "Categories",
    es: "Categorías",
  },
  seePlace: {
    es: "Ver página",
    en: "See place",
  },
  addAddress: {
    es: "Agrega una dirección",
    en: "Add an address",
  },
  primary: {
    es: "Primaria",
    en: "Primary",
  },
  secondary: {
    es: "Secundarias",
    en: "Secondary",
  },
  improvementsfooter: {
    en: "To improve",
    es: "Para mejorar",
  },
  improvementsfooterSort: {
    en: "# . update the order",
    es: "# . actualiza el orden",
  },
  improvementsfooterAdd: {
    en: "Add new categories",
    es: "Agregar nuevas categorias",
  },
  analyze: {
    en: "Last update: ",
    es: "Última actualización: ",
  },
  directionStatus: {
    INCOMPLETE: {
      en: "Incomplete address",
      es: "Dirección Incompleta",
    },
    MATCHED: {
      en: "Correct address",
      es: "Dirección correcta",
    },
    MISMATCHED: {
      en: "Mismatched direction",
      es: "Dirección no coincidente",
    },
  },
  summary: {
    captionTitle: {
      en: "Improve the following indicators to increase your score!",
      es: "Mejora los siguientes indicadores para subir tu score!",
    },
    placesWithoutKeywords: {
      en: "Locations with incorrect categories",
      es: "Locales con categorías incorrectas",
    },
    categoriesCorrect: {
      en: "Locations with correct categories",
      es: "Locales con categorías correctas",
    },  
    placesWithoutPosts: {
      en: "Locations without posts",
      es: "Locales sin Google Posts",
    },
    categoriesCorrectWithGooglePosts: {
      en: "With Google Posts",
      es: "Con Google Posts",
    },  
    last15: {
      en: "(last 15 days)",
      es: "(últimos 15 días)",
    },
    placesWithoutPhotos: {
      en: "Locations without photos",
      es: "Locales sin fotos",
    },
    categoriesCorrectWithFewPhotos: {
      en: "With 3 or more brand photos",
      es: "Con 3 o mas fotos de la marca",
    },  
    placesWithoutPhone: {
      en: "Locations without phone",
      es: "Locales sin teléfono",
    },
    categoriesCorrectWithPhone: {
      en: "With phone number",
      es: "Con teléfono",
    },  
    placesWithoutWebsite: {
      en: "Locations without website",
      es: "Locales sin sitio web",
    },
    categoriesCorrectWithWebsite: {
      en: "With correct website",
      es: "Con sitio web correcto",
    },  
    currentScore: {
      en: "Your current score is:",
      es: "Tu score actual es:",
    },
    seeVideo: {
      en: "Play video",
      es: "Ver video",
    },
    videoTitle: {
      en: "How to improve your Google My Business profile?",
      es: "¿Cómo mejorar tu perfil de Google My Business?",
    },
    videoSubtitle: {
      en: "Discover how to improve your Google My Business profile",
      es: "Descubre cómo mejorar tu perfil de Google My Business",
    },
    videoDescription: {
      en: "In this video we show you how to improve your Google My Business profile",
      es: "En este video te mostramos cómo mejorar tu perfil de Google My Business",
    },
    makePost: {
      en: "+ Post",
      es: "+ Postear",
    },
  },
  postHistoryCount: {
    en: "Posts",
    es: "Posts",
  },
  phone: {
    en: "Phone",
    es: "Teléfono",
  },
  notVerified: { es: "Sin verificar", en: "Unverified" },
  verified: { es: "Verificada", en: "Verified" },
  withoutKeywords: {
    es: "Problemas con Categorías",
    en: "Problems with Categories",
  },
  withoutPosts: {
    es: "Problemas con Google Posts",
    en: "Problems with Google Posts",
  },
  withoutPhotos: { es: "Problemas con Fotos", en: "Problems with Photos" },
  withoutPhone: { es: "Problemas con Teléfono", en: "Problems with Phone" },
  withoutWebsite: { es: "Problemas con Website ", en: "Problems with Website" },
  all: { es: "Ver Todos ", en: "All" },
  add: { es: "Agregar", en: "Add" },
  createPost: { es: "Crear post", en: "Create post" },
  noPosts: { es: "¡No tienes posts!", en: "No posts!" },
  score: {
    howItWorks: {
      es: "¿Cómo funciona el Score?",
      en: "How does the Score work?",
    },
  },
  btnActionables: {
    es: "Ver accionables",
    en: "View actionables",
  },
  btnActionablesInsignia: {
    es: "Ver local insignia",
    en: "View flagship location",
  },
  contentLocalInsignia: {
    textBold: {
      es: "Replica",
      en: "Replicate",
    },
    text: {
      es: "los datos de",
      en: "the data of",
    },
    textBold2: {
      es: "este Local Insignia en el resto de tus locales",
      en: "this flagship location in the rest of your locations",
    },
    text2: {
      es: "para aumentar el score de los mismos.",
      en: "to increase their score.",
    },
  },
};


export const BusinessProfilesTableContainer = withEventTracking(
  translate(BusinessProfilesTable)
);
